<template>
    <section
        v-if="hero"
        :class="$style.block"
        :style="{ '--fit-columns': Math.ceil(tiles.length / 2) }"
        data-interface-section="collections"
    >
        <BaseLink
            :to="getCategoryRoute({ hru: hero.hru, type: 'collection' })"
            :class="['sqrcl', $style.hero]"
            data-interface-target="main-collection"
        >
            <CardContainer
                :src="withWidth(hero.seo_picture, 448)"
                :sqrcl="false"
                alt=""
                :class="$style.cover_override"
            />
            <span :class="$style.caption">
                <span
                    :class="$style.month"
                    data-allow-mismatch="text"
                >
                    {{ month }}
                </span>
                <span :class="$style.title">{{ hero.title }}</span>
            </span>
        </BaseLink>
        <div
            v-if="tiles.length"
            :class="$style.tiles"
        >
            <div
                ref="grid"
                :class="$style.grid"
            >
                <button
                    type="button"
                    :class="[$style.tile, $style.dice]"
                    data-interface-target="collections-shuffle"
                    @click="reshuffled = shuffle(getCollectionTiles(props.collections))"
                >
                    <img
                        :src="diceIcon"
                        alt=""
                        loading="lazy"
                        :class="$style.icon"
                    />
                    <span :class="$style.label">{{ $tr('navigation.random') }}</span>
                </button>
                <BaseLink
                    v-for="(tile, index) in tiles"
                    :key="tile.hru"
                    :to="tile.to"
                    :title="tile.title"
                    :inert="index >= visibleTiles"
                    :class="$style.tile"
                    data-interface-target="collection"
                >
                    <img
                        :src="tile.icon"
                        alt=""
                        loading="lazy"
                        :class="$style.icon"
                    />
                    <span :class="$style.label">{{ tile.short_title || tile.title }}</span>
                </BaseLink>
            </div>
        </div>
    </section>
</template>
<script lang="ts" setup>
import { useResizeObserver } from '@vueuse/core'
import { computed, ref, useTemplateRef } from 'vue'

import BaseLink from '@/components/BaseLink.vue'
import CardContainer from '@/components/CardContainer.vue'
import { useUrl } from '@/composables/url-helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { withWidth } from '@/pages/Collection/preview-image'
import type { APIMainPageCollection } from '@/types'
import { shuffle } from '@/utils/helpers'

import { getCollectionTiles } from './collection-tiles'
import diceIcon from './icons/random.webp'

const props = defineProps<{ collections: APIMainPageCollection[] }>()

const { getCategoryRoute } = useUrl()
const { currentLocale } = useI18nAddon()

const reshuffled = ref<ReturnType<typeof getCollectionTiles>>()

const hero = computed(() => props.collections[0])
const tiles = computed(() =>
    (reshuffled.value ?? getCollectionTiles(props.collections)).map((tile) => ({
        ...tile,
        to: getCategoryRoute({ hru: tile.hru, type: 'collection' }),
    })),
)

const grid = useTemplateRef('grid')
const visibleTiles = ref(Infinity)
useResizeObserver(grid, () => {
    const { right } = grid.value!.getBoundingClientRect()
    visibleTiles.value = [...grid.value!.querySelectorAll('a')].filter(
        (tile) => tile.getBoundingClientRect().left < right,
    ).length
})

const month = computed(() => new Date().toLocaleDateString(currentLocale.value, { month: 'long', timeZone: 'UTC' }))
</script>
<style module>
.block {
    --u: 1rem;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-block: 24px;
}

.hero {
    display: grid;
    grid-template-areas: 'main';
    aspect-ratio: 16 / 9;
    border-radius: 24px;
    --sqrcl: clamp(0rem, 30%, 50rem);
    overflow: hidden;
    color: var(--white);
}

.cover_override {
    grid-area: main;
    width: 100%;
    height: 100%;
    aspect-ratio: auto;
    border-radius: 0;
}

.caption {
    grid-area: main;
    position: relative;
    min-width: 0;
    background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 50%,
        rgba(124, 85, 176, 0.43) 73.418%,
        rgba(67, 33, 110, 0.91) 96.008%
    );
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    gap: calc(4 * var(--u));
    padding: 16px 24px;
}

.month {
    padding: calc(4 * var(--u));
    border-radius: calc(40 * var(--u));
    background: var(--white);
    color: var(--black);
    font-size: calc(8 * var(--u));
    font-weight: var(--font-weight-bold);
    line-height: 1.1;
    text-transform: uppercase;
}

.title {
    max-width: 100%;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    font-size: var(--font-size-header-xs);
    font-weight: var(--font-weight-accent);
    line-height: 1.1;
}

.hero:focus-visible .caption {
    box-shadow: inset 0 0 0 2px var(--purple-600);
}

.grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px;
}

.tile {
    box-sizing: border-box;
    aspect-ratio: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: calc(2 * var(--u));
    padding: calc(4 * var(--u)) calc(4 * var(--u)) calc(6 * var(--u));
    border: none;
    border-radius: calc(16 * var(--u));
    background: var(--white-04);
    color: var(--white-80);
    font-family: inherit;
    cursor: pointer;
    transition:
        background-color 0.2s,
        color 0.2s;
}

.tile:hover,
.tile:focus-visible {
    background: rgba(179, 98, 243, 0.13);
    color: var(--purple-600);
}

.icon {
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    max-width: calc(60 * var(--u));
    max-height: calc(60 * var(--u));
    object-fit: contain;
    transition: transform 0.3s ease-out;
    will-change: transform;
}

.tile:hover .icon,
.tile:focus-visible .icon {
    transform: translateY(-3.6%) scale(1.03);
    transition: transform 0.56s linear(0, 0.6 12%, 1.25 30%, 0.92 50%, 1.04 68%, 0.99 84%, 1);
}

@media (prefers-reduced-motion: reduce) {
    .icon,
    .tile:hover .icon,
    .tile:focus-visible .icon {
        transition: none;
    }
}

.label {
    --label-u: calc((var(--u) + 1rem) / 2);
    width: 100%;
    overflow: hidden;
    font-size: calc(8 * var(--label-u));
    font-weight: var(--font-weight-accent);
    line-height: 1.1;
    letter-spacing: calc(0.08 * var(--label-u));
    text-align: center;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
}

.dice {
    display: none;
}

@media (--tablet-plus) {
    .block {
        --tile: 80;
        --gap: 4;
        --hero: calc(2 * var(--tile) + var(--gap));
        --hero-gap: 8;
        --max-scale: 2;
        flex-direction: row;
        align-items: flex-start;
        gap: 0;
    }

    .hero {
        flex: none;
        width: calc(var(--hero) * var(--u));
        aspect-ratio: 1;
        border-radius: calc(24 * var(--u));
        --sqrcl: calc(48 * var(--u));
    }

    .caption {
        justify-content: space-between;
        padding: calc(8 * var(--u)) calc(12 * var(--u)) calc(12 * var(--u));
    }

    .title {
        -webkit-line-clamp: 4;
        font-size: calc(14 * var(--u));
        line-height: 1;
    }

    /* without container queries (iOS 15) every tile wraps into view and there is no dice */
    .tiles {
        flex: 1 1 0;
        min-width: 0;
        margin-left: calc(var(--hero-gap) * var(--u));
    }

    .grid {
        grid-template-columns: repeat(auto-fill, calc(var(--tile) * var(--u)));
        grid-auto-rows: calc(var(--tile) * var(--u));
        gap: calc(var(--gap) * var(--u));
    }

    .tile {
        width: calc(var(--tile) * var(--u));
        aspect-ratio: auto;
    }

    @supports (container-type: size) {
        .block {
            --tiles-width: calc(var(--fit-columns) * (var(--tile) + var(--gap)) - var(--gap));
            --block-width: calc(var(--hero) + var(--hero-gap) + var(--tiles-width));
            container-type: inline-size;
            --u: clamp(1rem, 100cqw / var(--block-width), var(--max-scale) * 1rem);
        }

        /* 0px tall while every tile fits, 1px otherwise: the grid overflows it and the dice queries it */
        .tiles {
            --tie-towards-dice: 0.5;
            container-type: size;
            height: clamp(0rem, ((var(--block-width) + var(--tie-towards-dice)) * 1px - 100cqw) * 999, 1px);
        }

        .grid {
            --fit-slack: 0.1;
            --u: clamp(1rem, 100cqw / (var(--tiles-width) + var(--fit-slack)), var(--max-scale) * 1rem);
            height: calc(var(--hero) * var(--u));
            grid-template-rows: repeat(2, calc(var(--tile) * var(--u)));
            grid-auto-columns: calc(2 * var(--tile) * var(--u));
            grid-auto-flow: column;
            overflow-x: clip;
        }

        .tile {
            justify-self: end;
        }

        .dice {
            grid-area: 2 / -2;
        }

        @container (height > 0px) {
            .dice {
                display: flex;
            }
        }
    }
}
</style>
